import React, { useEffect, useState } from "react";
import PageContainer from "../../containers/PageContainer";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  supabase,
  getInvoiceFromPaymentIntent,
  processDocument,
  getPreMadeDocument,
} from "../../utils/supabase";
import { useOrder } from "../../context/Order.context";
import { useUser } from "../../context/User.context";
import { Blocks } from "react-loader-spinner";

type Props = {};

const PaymentConfirmation = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(true);

  // Get URL Params
  const params = new URLSearchParams(window.location.search);
  const paymentIntent = params.get("payment_intent");
  const paymentClientSecret = params.get("payment_intent_client_secret");

  const { auth_id } = useUser();
  const order_id = localStorage.getItem("order_number");

  // Map product IDs to document type names
  // These must match the keys in the backend DOCUMENT_PATHS mapping
  const getDocumentTypeFromProductId = (product_id: number): string => {
    const mapping: { [key: number]: string } = {
      1: 'individual-to-corporate-trustee', // Product 1: Individual to Corporate Trustee
      2: 'smsf-trust-deed-update',          // Product 2: SMSF Trust Deed Update
      3: 'death-benefit-agreement',         // Product 3: Death Benefit Agreement
    };

    return mapping[product_id] || 'individual-to-corporate-trustee';
  };

  // Confirm payment intent

  const confirmOrderAndCreateDocument = async () => {
    try {
      if (!paymentIntent || !paymentClientSecret) {
        console.error("❌ Payment Confirmation Error: Missing payment intent", {
          timestamp: new Date().toISOString(),
          location: "PaymentConfirmation.tsx",
          paymentIntent: paymentIntent ? "present" : "missing",
          paymentClientSecret: paymentClientSecret ? "present" : "missing",
        });
        return;
      }

      console.log("🔄 Confirming payment and creating document...", {
        timestamp: new Date().toISOString(),
        paymentIntent: "***" + paymentIntent.slice(-8),
        order_id,
      });

      const receiptRequest = await getInvoiceFromPaymentIntent(
        paymentIntent,
        auth_id as string,
        order_id as string
      );
      const receiptUrl = receiptRequest.invoice_url;

      console.log("✅ Invoice retrieved:", {
        invoiceUrl: receiptUrl ? "received" : "not available",
      });

      const { data, error } = await supabase
        .from("document_orders")
        .update({
          status: "Paid",
          stripe_payment_id: paymentIntent,
          invoice_url: receiptUrl ? receiptUrl : null,
        })
        .eq("id", order_id)
        .select();

      if (error) {
        console.error("❌ Database Update Error:", {
          timestamp: new Date().toISOString(),
          location: "PaymentConfirmation.tsx - updating document_orders",
          error: error.message,
          details: error,
        });
      } else {
        console.log("✅ Order status updated to 'Paid':", {
          order_id,
          product_id: data[0]?.product_id,
          has_data: !!data,
          data_length: data?.length,
        });
      }

      // INTERIM SOLUTION: Fetch pre-made document instead of generating
      console.log("🔍 Checking document fetch conditions:", {
        has_data: !!data,
        data_length: data?.length,
        product_id: data?.[0]?.product_id,
        should_fetch: !!(data && data[0]?.product_id),
      });

      if (data && data[0]?.product_id) {
        const documentType = getDocumentTypeFromProductId(data[0].product_id);

        console.log("🔄 Fetching pre-made document...", {
          timestamp: new Date().toISOString(),
          documentType,
          order_id,
          auth_id: auth_id?.substring(0, 8) + "...",
        });

        try {
          const documentBlob = await getPreMadeDocument(
            documentType,
            auth_id as string,
            order_id as string
          );

          console.log("✅ Document fetched successfully:", {
            timestamp: new Date().toISOString(),
            documentType,
            size: documentBlob?.size || "unknown",
          });

          // Verify that the backend actually stored the document by re-querying the order
          const { data: verifyData, error: verifyError } = await supabase
            .from("document_orders")
            .select("file_url")
            .eq("id", order_id)
            .single();

          if (verifyError) {
            console.error("❌ Failed to verify document storage:", verifyError);
          } else if (!verifyData?.file_url) {
            console.warn("⚠️  Document fetched but file_url not updated in database");
            throw new Error("Document was not properly stored. Please contact support.");
          } else {
            console.log("✅ Verified document stored with URL:", verifyData.file_url);
          }
        } catch (docError: any) {
          console.error("❌ Error fetching/storing pre-made document:", {
            timestamp: new Date().toISOString(),
            error: docError?.message,
            response: docError?.response?.data,
            stack: docError?.stack,
          });

          // Show error to user - this is critical, don't hide it
          alert(
            "Warning: There was an issue storing your document. " +
            "Your payment was successful and we have your order. " +
            "Please contact support if you cannot access your document.\n\n" +
            "Error: " + (docError?.response?.data?.error || docError?.message)
          );
        }

        // Note: The backend already updates document_orders with file_url
      } else {
        console.warn("⚠️  Skipping document fetch - missing data or product_id");
      }

      localStorage.removeItem("order_number");

      console.log("✅ Payment confirmation complete", {
        timestamp: new Date().toISOString(),
      });

      setLoading(false);
    } catch (error: any) {
      console.error("❌ Payment Confirmation Error:", {
        timestamp: new Date().toISOString(),
        location: "PaymentConfirmation.tsx - confirmOrderAndCreateDocument",
        error: error?.message || "Unknown error",
        response: error?.response?.data || null,
        stack: error?.stack,
      });
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    console.log(order_id, auth_id);
    if (order_id && auth_id) {
      confirmOrderAndCreateDocument();
    }
  }, [auth_id, order_id]);

  return (
    <PageContainer>
      {loading === false && (
        <div className="w-full min-h-screen gap-y-4 flex flex-col align-middle items-center mt-20">
          <h2 className="text-4xl font-semibold text-jd-grey">
            Thank you for your purchase
          </h2>
          <p className="text-jd-blue">
            Your document has been processed successfully.
          </p>
          <p className="text-jd-blue">
            Your documents are available under the order section.
          </p>
          <Button
            onClick={() => navigate("/customer/orders", { replace: true })}
            className="w-1/4"
          >
            Go to Orders
          </Button>
        </div>
      )}
      {loading === true && (
        <div className="w-full min-h-screen gap-y-4 flex flex-col align-middle items-center mt-20">
          <h2 className="text-4xl font-semibold text-jd-grey">
            We are processing your document. Please don't navigate away from
            this page yet
          </h2>
          <Blocks
            height={200}
            width={200}
            color="#4fa94d"
            ariaLabel="blocks-loading"
            wrapperStyle={{}}
            wrapperClass="blocks-wrapper"
            visible={true}
          />
        </div>
      )}
    </PageContainer>
  );
};

export default PaymentConfirmation;
